import * as React from 'react'
import { Form, Spin, Button, Input, Tabs, Dropdown, Menu, message } from 'antd'
import { set, get, cloneDeep } from 'lodash'
import produce from 'immer'
import { showError, resourceNotFound, ValidationError } from 'helpers/errors'
import { moveByDelta, HARD_SPACE, strEqual } from 'helpers/utils'
import {
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  validateUsername,
  isReadOnly,
} from 'helpers/formViews'
import permissionAssignmentTypes from 'options/users/permissionAssignmentTypes'
import approverSelectionTypes from 'options/users/approverSelectionTypes'
import reqApprovalTypes from 'options/users/reqApprovalTypes'
import orderApprovalTypes from 'options/users/orderApprovalTypes'
import { getUserTagSettings, getUserTagSettingItems } from 'helpers/procurement'
import linkTargets from 'options/linkTargets'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import FormButtons from 'elements/FormButtons'
import Select, { Option, getOptionProps } from 'elements/Select'
import Checkbox from 'elements/Checkbox'
import CheckboxList from 'elements/CheckboxList'
import Icon from 'elements/Icon'
import InputNumber from 'elements/InputNumber'
import Tags from 'containers/Users/FormView/Tags'
import Permissions from 'containers/Users/FormView/Permissions'
import Divider from 'elements/Divider'
import Modal from 'elements/Modal'
import Drawer from 'elements/Drawer'
import { Row, Col } from 'elements/Grid'

const OperatorsFormView = linkTargets.operatorRecord.formComponent

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
  }

  componentDidMount() {
    this.fetchItem()
  }

  saveItemSuccess = (savedItem) =>
    strEqual(savedItem.userName, this.props.user.userName)
      ? this.props.refreshToken(true)
      : Promise.resolve(true)

  getItemId = () => {
    const userName = [this.state.item?.userName, this.props.linkTargetRecord?.userName].find(Boolean)

    return userName ? encodeURIComponent(userName) : null
  }

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const hasProfilePriority = this.props.customer.generalSettings.profilePriority === 'HighestOrder'
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
      ])

      const item = cloneDeep(responses[0].value.data)

      if (!item.id && this.props.tenant.ssoEnabled) {
        item.password = item.confirmPassword = `pwd${Date.now()}!`
      }

      const [locations, suppliers, customers, userGroups, profiles, users] = await Promise.all([
        this.props.getLocations({ includeAllLocations: true }),
        this.props.getSuppliers(),
        this.props.userIsDcribDistributor
          ? this.props.getCustomers({ ignoreUserAuthorization: true })
          : Promise.resolve(null),
        this.props.getUserGroups(),
        this.props.getProfiles(),
        this.props.getUsers(),
      ])

      this.setState({
        item,
        fieldSettings: get(responses[1], 'value.data.fieldSettings', []),
        locations: get(locations, 'value.data.items', []),
        suppliers: get(suppliers, 'value.data.items', []),
        customers: get(customers, 'value.data.items', []),
        userGroups: get(userGroups, 'value.data.items', []),
        profiles: hasProfilePriority
          ? get(profiles, 'value.data.items', [])
              .slice()
              .sort((a, b) => {
                const m = item.profileIds.indexOf(a.profileId)
                const n = item.profileIds.indexOf(b.profileId)
                return m === -1 && n !== -1 ? 1 : m !== -1 && n === -1 ? -1 : m - n
              })
          : get(profiles, 'value.data.items', []),
        users: get(users, 'value.data.items', [])
          .slice()
          .sort((a, b) => {
            const m = item.coreUserSettings.approvers.indexOf(a.userName)
            const n = item.coreUserSettings.approvers.indexOf(b.userName)
            return m === -1 && n !== -1 ? 1 : m !== -1 && n === -1 ? -1 : m - n
          }),
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  handleActionsMenuClick = async ({ key }) => {
    switch (key) {
      case 'copyFromExistingRecord':
        this.setState({
          copyFromExistingVisible: true,
          copyFromExistingSelectedUserName: '',
        })
        break

      case 'createOperatorFromUser':
        this.setState({
          createOperatorFromUserVisible: true,
          createOperatorFromUserSaving: false,
        })
        break

      case 'unlock':
        try {
          await this.props.onSave?.(true)
          await this.props.unlock(this.state.item)

          message.info(t('success'))
        } catch (error) {
          showError({ error })
        } finally {
          await this.props.onSave?.(false)
        }
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  handleTagsChange = (values) =>
    this.setState(
      produce((draft) => {
        draft.item.coreUserSettings.tagSettings = getUserTagSettings(values)
      })
    )

  handlePermissionsChange = (values) =>
    this.setState(
      produce((draft) => {
        draft.item.coreUserSettings.permissions = values
      })
    )

  moveHighlightedProfileId = (delta) =>
    this.setState(
      produce((draft) => {
        draft.profiles = moveByDelta(
          draft.profiles,
          draft.profiles.findIndex((one) => one.profileId === draft.highlightedProfileId),
          delta
        )
        draft.item.profileIds = moveByDelta(
          draft.item.profileIds,
          draft.item.profileIds.findIndex((one) => one === draft.highlightedProfileId),
          delta
        )
      })
    )

  moveHighlightedApprover = (delta) =>
    this.setState(
      produce((draft) => {
        draft.users = moveByDelta(
          draft.users,
          draft.users.findIndex((one) => one.userName === draft.highlightedApprover),
          delta
        )
        draft.item.coreUserSettings.approvers = moveByDelta(
          draft.item.coreUserSettings.approvers,
          draft.item.coreUserSettings.approvers.findIndex((one) => one === draft.highlightedApprover),
          delta
        )
      })
    )

  handleCopyFromExisting = async () => {
    try {
      this.setState({ copyFromExistingLoading: true })

      const existing = await this.props
        .getItem(this.state.copyFromExistingSelectedUserName)
        .then((r) => r.value.data)
        .then(cloneDeep)

      this.setState(
        produce((draft) => {
          draft.item.permissionAssignmentType = existing.permissionAssignmentType
          draft.item.profileIds = existing.profileIds
          draft.item.coreUserSettings = existing.coreUserSettings
          draft.copyFromExistingVisible = false
          draft.copyFromExistingSelectedUserName = ''
        })
      )
    } catch (error) {
      showError({ error })
    } finally {
      this.setState({ copyFromExistingLoading: false })
    }
  }

  render() {
    const { form, customer, userIsInRoleSupplier, userIsInRoleDistributor, tenantGroupIsCardinal } =
      this.props
    const { item, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(this.state.fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, this.state.fieldSettings)
    const readOnly = isReadOnly(this)
    const hasProfilePriority = customer.generalSettings.profilePriority === 'HighestOrder'
    const profileBasedPermissionAssignment = item.permissionAssignmentType === 'ProfileBased'
    const userDefinedApproverSelection = item.coreUserSettings.approverSelection === 'UserDefinedList'

    return (
      <>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          <Dropdown
            overlay={
              <Menu onClick={this.handleActionsMenuClick}>
                {!userIsInRoleSupplier &&
                  !userIsInRoleDistributor && [
                    <Menu.Item key="createOperatorFromUser" disabled={!item.id}>
                      {t('createOperatorFromUser')}
                    </Menu.Item>,
                    <Menu.Divider key="divider1" />,
                  ]}
                <Menu.Item key="copyFromExistingRecord" disabled={readOnly}>
                  {t('copyFromUserRecord')}
                </Menu.Item>
                {!userIsInRoleSupplier &&
                  item.id &&
                  item.isLockedOut && [
                    <Menu.Divider key="divider2" />,
                    <Menu.Item key="unlock">{t('unlock')}</Menu.Item>,
                  ]}
              </Menu>
            }
            trigger={['click']}
          >
            <Button className="form-items-actions">
              {t('actions')}
              <Icon type="KeyboardArrowDown" />
            </Button>
          </Dropdown>
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <Row>
              <Col third>
                <Form.Item label={createLabel('userName')}>
                  {createFieldDecorator(
                    'userName',
                    !item.id ? { extraRules: [validateUsername()] } : {}
                  )(<Input disabled={Boolean(item.id)} autoCorrect="off" autoCapitalize="none" autoFocus />)}
                </Form.Item>
                <Form.Item label={createLabel('fullname')}>
                  {createFieldDecorator('fullname')(<Input readOnly={readOnly} />)}
                </Form.Item>
                <Form.Item label={createLabel('email')}>
                  {createFieldDecorator('email')(<Input type="email" readOnly={readOnly} />)}
                </Form.Item>
              </Col>
              <Col third>
                <Form.Item label={createLabel('userPhone')}>
                  {createFieldDecorator('userPhone')(<Input type="tel" readOnly={readOnly} />)}
                </Form.Item>
                <Form.Item label={createLabel('title')}>
                  {createFieldDecorator('title')(<Input readOnly={readOnly} />)}
                </Form.Item>
                {!userIsInRoleSupplier && !userIsInRoleDistributor && (
                  <Form.Item label={createLabel('permissionAssignmentType')}>
                    <Select
                      value={item.permissionAssignmentType}
                      onChange={(value) => this.setItemValue('permissionAssignmentType', value)}
                      readOnly={readOnly}
                      allowClear={false}
                    >
                      {Object.entries(permissionAssignmentTypes).map(([key, value]) => (
                        <Option key={key} value={key}>
                          {t(value)}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </Col>
              <Col third>
                {!item.id && !this.props.tenant.ssoEnabled && (
                  <>
                    <Form.Item label={createLabel('password')}>
                      {createFieldDecorator('password')(<Input.Password type="password" />)}
                    </Form.Item>
                    <Form.Item label={createLabel('confirmPassword')}>
                      {createFieldDecorator('confirmPassword')(<Input.Password type="password" />)}
                    </Form.Item>
                  </>
                )}
                <Row>
                  <Col xs={12}>
                    <Form.Item label={HARD_SPACE}>
                      {createFieldDecorator('active', { valuePropName: 'checked' })(
                        <Checkbox readOnly={readOnly}>{createLabel('active')}</Checkbox>
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item label={HARD_SPACE}>
                      {createFieldDecorator('outOfOffice', { valuePropName: 'checked' })(
                        <Checkbox readOnly={readOnly}>{createLabel('outOfOffice')}</Checkbox>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
            {!userIsInRoleSupplier && (
              <Tabs>
                {profileBasedPermissionAssignment && (
                  <Tabs.TabPane key="profiles" tab={t('profiles')} forceRender>
                    <Row>
                      <Col md={12} xs={24}>
                        <Row type="flex" justify="space-between" align="middle">
                          <Col xs={hasProfilePriority ? 20 : 24}>
                            <Form.Item label={createLabel('profileIds')}>
                              <CheckboxList
                                dataSource={this.state.profiles}
                                value={item.profileIds}
                                onChange={(values) => this.setItemValue('profileIds', values)}
                                readOnly={readOnly}
                                {...(hasProfilePriority
                                  ? {
                                      onHighlight: (value) => this.setState({ highlightedProfileId: value }),
                                      highlighted: this.state.highlightedProfileId,
                                    }
                                  : {})}
                              />
                            </Form.Item>
                          </Col>
                          {hasProfilePriority && (
                            <Col xs={4}>
                              <Button
                                className="mb-12"
                                onClick={() => this.moveHighlightedProfileId(-1)}
                                disabled={!this.state.highlightedProfileId}
                              >
                                <Icon type="KeyboardArrowUp" />
                              </Button>
                              <Button
                                onClick={() => this.moveHighlightedProfileId(1)}
                                disabled={!this.state.highlightedProfileId}
                              >
                                <Icon type="KeyboardArrowDown" />
                              </Button>
                            </Col>
                          )}
                        </Row>
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                )}
                <Tabs.TabPane key="permissions" tab={t('permissions')} forceRender>
                  <Permissions
                    values={item.coreUserSettings.permissions}
                    onChange={this.handlePermissionsChange}
                    readOnly={readOnly}
                    disabled={profileBasedPermissionAssignment}
                  />
                </Tabs.TabPane>
                {!userIsInRoleDistributor && (
                  <Tabs.TabPane key="locations" tab={t('locations')} forceRender>
                    <Row>
                      <Col md={12} xs={24}>
                        <Form.Item label={createLabel('coreUserSettings.locationIds')}>
                          <CheckboxList
                            dataSource={this.state.locations}
                            value={
                              JSON.stringify(item.coreUserSettings.locationIds) !== '[0]'
                                ? item.coreUserSettings.locationIds
                                : this.state.locations.map((each) => each.id)
                            }
                            onChange={(values) =>
                              this.setItemValue(
                                'coreUserSettings.locationIds',
                                values.length === this.state.locations.length ? [0] : values
                              )
                            }
                            readOnly={readOnly}
                            disabled={profileBasedPermissionAssignment}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                )}
                {!userIsInRoleDistributor && (
                  <Tabs.TabPane key="groups" tab={t('groups')} forceRender>
                    <Row>
                      <Col md={12} xs={24}>
                        <Form.Item label={createLabel('coreUserSettings.userGroupIds')}>
                          <CheckboxList
                            dataSource={this.state.userGroups}
                            value={item.coreUserSettings.userGroupIds}
                            onChange={(values) => this.setItemValue('coreUserSettings.userGroupIds', values)}
                            readOnly={readOnly}
                            disabled={profileBasedPermissionAssignment}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                )}
                {!userIsInRoleDistributor && (
                  <Tabs.TabPane key="approvals" tab={t('approvals')} forceRender>
                    <Row>
                      <Col md={10} xs={24}>
                        <Form.Item label={createLabel('coreUserSettings.approverSelection')}>
                          {createFieldDecorator('coreUserSettings.approverSelection')(
                            <Select
                              onChange={(value) =>
                                this.setItemValue('coreUserSettings.approverSelection', value)
                              }
                              readOnly={readOnly}
                              allowClear={false}
                              disabled={profileBasedPermissionAssignment}
                            >
                              {Object.entries(approverSelectionTypes).map(([key, value]) => (
                                <Option key={key} value={key}>
                                  {t(value)}
                                </Option>
                              ))}
                            </Select>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Divider className="my-12" />
                    <Row>
                      <Col md={12} xs={24}>
                        <Row type="flex" justify="space-between" align="middle">
                          <Col xs={20}>
                            <Form.Item label={createLabel('coreUserSettings.approvers')}>
                              <CheckboxList
                                idField="userName"
                                dataSource={this.state.users}
                                value={item.coreUserSettings.approvers}
                                onChange={(values) => this.setItemValue('coreUserSettings.approvers', values)}
                                readOnly={readOnly}
                                onHighlight={(value) => this.setState({ highlightedApprover: value })}
                                highlighted={this.state.highlightedApprover}
                                disabled={profileBasedPermissionAssignment || !userDefinedApproverSelection}
                                itemsStyle={{ maxHeight: '240px' }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={4}>
                            <Button
                              className="mb-12"
                              onClick={() => this.moveHighlightedApprover(-1)}
                              disabled={!this.state.highlightedApprover}
                            >
                              <Icon type="KeyboardArrowUp" />
                            </Button>
                            <Button
                              onClick={() => this.moveHighlightedApprover(1)}
                              disabled={!this.state.highlightedApprover}
                            >
                              <Icon type="KeyboardArrowDown" />
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                      <Col md={12} xs={24}>
                        <Form.Item label={createLabel('coreUserSettings.reqApproval')}>
                          {createFieldDecorator('coreUserSettings.reqApproval')(
                            <Select
                              readOnly={readOnly}
                              allowClear={false}
                              disabled={profileBasedPermissionAssignment || !userDefinedApproverSelection}
                            >
                              {Object.entries(reqApprovalTypes).map(([key, value]) => (
                                <Option key={key} value={key}>
                                  {t(value)}
                                </Option>
                              ))}
                            </Select>
                          )}
                        </Form.Item>
                        <Form.Item label={createLabel('coreUserSettings.orderApproval')}>
                          {createFieldDecorator('coreUserSettings.orderApproval')(
                            <Select
                              readOnly={readOnly}
                              allowClear={false}
                              disabled={profileBasedPermissionAssignment || !userDefinedApproverSelection}
                            >
                              {Object.entries(orderApprovalTypes).map(([key, value]) => (
                                <Option key={key} value={key}>
                                  {t(value)}
                                </Option>
                              ))}
                            </Select>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                )}
                {!userIsInRoleDistributor && (
                  <Tabs.TabPane key="tags" tab={t('tags')} forceRender>
                    {createFieldDecorator('coreUserSettings.overrideTags', {
                      valuePropName: 'checked',
                    })(
                      <Checkbox readOnly={readOnly} disabled={profileBasedPermissionAssignment}>
                        {createLabel('coreUserSettings.overrideTags')}
                      </Checkbox>
                    )}
                    <Tags
                      items={getUserTagSettingItems(item.coreUserSettings.tagSettings)}
                      onChange={this.handleTagsChange}
                      readOnly={readOnly}
                      disabled={
                        profileBasedPermissionAssignment ||
                        form.getFieldValue('coreUserSettings.overrideTags') !== true
                      }
                    />
                  </Tabs.TabPane>
                )}
                {!userIsInRoleDistributor && tenantGroupIsCardinal && (
                  <Tabs.TabPane key="suppliers" tab={t('suppliers')} forceRender>
                    <Row>
                      <Col md={12} xs={24}>
                        <Form.Item label={createLabel('coreUserSettings.supplierIds')}>
                          <CheckboxList
                            dataSource={this.state.suppliers}
                            value={item.coreUserSettings.supplierIds}
                            onChange={(values) => this.setItemValue('coreUserSettings.supplierIds', values)}
                            readOnly={readOnly}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                )}
                {customer.passwordSettings.definitionMode === 'Advanced' && !userIsInRoleDistributor && (
                  <Tabs.TabPane key="password" tab={t('password')} forceRender>
                    <Row>
                      <Col third>
                        <Form.Item label={createLabel('coreUserSettings.passwordExpirationDays')}>
                          {createFieldDecorator('coreUserSettings.passwordExpirationDays')(
                            <InputNumber
                              min={0}
                              readOnly={readOnly}
                              disabled={profileBasedPermissionAssignment}
                            />
                          )}
                        </Form.Item>
                        {createFieldDecorator('coreUserSettings.passwordMustChangeOnNextLogin', {
                          valuePropName: 'checked',
                        })(
                          <Checkbox readOnly={readOnly} disabled={profileBasedPermissionAssignment}>
                            {createLabel('coreUserSettings.passwordMustChangeOnNextLogin')}
                          </Checkbox>
                        )}
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                )}
                {userIsInRoleDistributor && (
                  <Tabs.TabPane key="customers" tab={t('customers')} forceRender>
                    <Row>
                      <Col md={12} xs={24}>
                        <Form.Item label={createLabel('coreUserSettings.customerIds')}>
                          <CheckboxList
                            dataSource={this.state.customers}
                            value={
                              JSON.stringify(item.coreUserSettings.customerIds) !== '[0]'
                                ? item.coreUserSettings.customerIds
                                : this.state.customers.map((each) => each.id)
                            }
                            onChange={(values) =>
                              this.setItemValue(
                                'coreUserSettings.customerIds',
                                values.length === this.state.customers.length ? [0] : values
                              )
                            }
                            readOnly={readOnly}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                )}
              </Tabs>
            )}
          </div>
          <div className="form-buttons-container">
            <FormButtons
              readOnly={readOnly}
              onCancel={this.props.onCancel}
              onSubmit={this.handleSubmit}
              saveButtonLoading={this.state.saveButtonLoading}
              saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
            />
          </div>
        </Form>
        <Modal
          title={t('selectUser')}
          visible={this.state.copyFromExistingVisible}
          okText={t('copy')}
          onOk={this.handleCopyFromExisting}
          okButtonProps={{
            disabled: !this.state.copyFromExistingSelectedUserName,
            loading: this.state.copyFromExistingLoading,
          }}
          onCancel={() =>
            this.setState({
              copyFromExistingVisible: false,
              copyFromExistingSelectedUserName: '',
            })
          }
        >
          <Form.Item colon={false} label={t('user')}>
            <Select
              onChange={(value) => this.setState({ copyFromExistingSelectedUserName: value })}
              placeholder={t('select')}
            >
              {(this.state.users ?? [])
                .filter((each) => each.userName !== item.userName)
                .map((each) => (
                  <Option key={each.userName} value={each.userName}>
                    <span {...getOptionProps(each)}>{each.displayName}</span>
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Modal>
        <Drawer
          title={t('createOperatorFromUser')}
          size={linkTargets.operatorRecord.formSize}
          visible={this.state.createOperatorFromUserVisible}
          onClose={() => this.setState({ createOperatorFromUserVisible: false })}
          saving={this.state.createOperatorFromUserSaving}
        >
          <OperatorsFormView
            name={form.getFieldValue('fullname')}
            email={form.getFieldValue('email')}
            userName={form.getFieldValue('userName')}
            onSave={(pending) => this.setState({ createOperatorFromUserSaving: pending })}
            onSaveAndClose={() => this.setState({ createOperatorFromUserVisible: false })}
            onCancel={() => this.setState({ createOperatorFromUserVisible: false })}
          />
        </Drawer>
      </>
    )
  }
}

export default Form.create()(FormView)
